import { WasteClassification } from "generated-graphql/graphql";

export interface MergeStreamLike {
  id: string;
  name: string;
  classification?: WasteClassification | null;
}

export interface ContainerLike {
  id: string;
  containerLabel: string;
  wasteStream?: {
    id: string;
    name: string;
    classification?: WasteClassification | null;
  } | null;
}

export interface MergeContainerRow {
  id: string;
  label: string;
  originalStreamId: string;
  originalStreamName: string;
  originalClassification: WasteClassification | null;
  /** True when the container moves off a merged-away stream onto the target. */
  repointed: boolean;
  /** True when repointing changes the container's classification (source class ≠ target). */
  classificationChanges: boolean;
}

export interface MergeContainerImpact {
  /** Every container that will live on the resulting merged stream (target's + repointed). */
  containers: MergeContainerRow[];
  totalCount: number;
  /** Subset of `containers` whose classification changes — drives the acknowledgment gate. */
  mismatches: MergeContainerRow[];
}

/**
 * Container impact of a merge. The target is the first selected stream (kept);
 * the rest are merged away and their containers repointed onto it. The result
 * lists every container that will end up on the merged stream — the target's
 * own (which stay) and the repointed ones — each with the stream it came from.
 * A container "changes classification" only when it is repointed from a stream
 * whose classification differs from the target's.
 */
export function computeMergeContainerImpact(
  selectedRows: MergeStreamLike[],
  containers: ContainerLike[]
): MergeContainerImpact {
  if (selectedRows.length === 0) {
    return { containers: [], totalCount: 0, mismatches: [] };
  }

  const [target] = selectedRows;
  const targetClassification = target.classification ?? null;

  const rows: MergeContainerRow[] = [];
  for (const container of containers) {
    const stream = container.wasteStream;
    if (stream == null) continue;
    const repointed = stream.id !== target.id;
    const originalClassification = stream.classification ?? null;
    rows.push({
      id: container.id,
      label: container.containerLabel,
      originalStreamId: stream.id,
      originalStreamName: stream.name,
      originalClassification,
      repointed,
      classificationChanges: repointed && originalClassification !== targetClassification,
    });
  }

  // Repointed rows first (what changes), then stable by label within each group.
  rows.sort((a, b) => {
    if (a.repointed !== b.repointed) return a.repointed ? -1 : 1;
    return a.label.localeCompare(b.label);
  });

  return {
    containers: rows,
    totalCount: rows.length,
    mismatches: rows.filter((row) => row.classificationChanges),
  };
}
